import { index, pgTable, text, timestamp, uuid, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';
import { users } from './users';

// =============================================================================
// DRIVE EXPORT — "Save to Google Drive" (docs/design/drive-export-2026-07-05.md).
// Re-landed on the live tree (DRIVE-RELAND lane, 2026-07-05) after the DRIVE-BUILD
// lane built this against a stale June-20 world (pre-apps/throughline). Logic is
// carried forward; the ONE schema correction this lane makes is user_id's TYPE.
//
// user_id is `uuid`, NOT `text` — CLAUDE.md's corrected 2026-06-22 law: "identity
// = per-user users.id uuid, text-keyed user_id tables are migration debt." The
// prior lane's reference copied the OLDER `resumes`/`jobs`/`coverLetters`-era
// text convention (still debt on those pre-existing tables, left untouched —
// FROZEN foundations, additive-only). This is a NEW table, so — exactly like
// entitlement_grants.ts and presence_profiles.ts, the two newest precedents in
// this package — it takes the CURRENT-LAW shape from day one rather than
// perpetuating the debt into a brand new table. userId carries a real FK to
// users.id (both sides uuid, mirroring presence_profiles.ts's own userId column);
// artifactId (below) stays text — it points at resumes.id/coverLetters.id, which
// are still text-keyed elsewhere (FROZEN, untouched by this additive-only lane).
//
// NO TOKEN MATERIAL lives here. The Drive refresh token is a secret →
// SecretsService (rule #9), stored under a per-user key
// (`drive_refresh_token:<userId>`) and resolved via getSecretValue(name, userId)
// at request time. This table only holds the pointer + grant metadata.
// =============================================================================

/**
 * Per-user "Save to Google Drive" connection. One row per user — revoking and
 * reconnecting UPDATES this same row (scope/grantedAt refreshed, revokedAt
 * cleared) rather than inserting a duplicate; the audit trail of every actual
 * export lives in driveExports below, never deleted (nothing-unrecoverable).
 */
export const userDriveGrants = pgTable(
  'user_drive_grants',
  {
    id: id(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    scope: text('scope').notNull(),
    /** The app-created "Career Documents — Mira" folder id, once it exists. */
    folderId: text('folder_id'),
    grantedAt: timestamp('granted_at', { withTimezone: true }),
    /** NULL = live grant. Set the instant a refresh fails with invalid_grant,
     *  or a human disconnects — never deleted (nothing-unrecoverable); a
     *  reconnect clears this back to NULL on the same row. */
    revokedAt: timestamp('revoked_at', { withTimezone: true }),
    ...timestamps,
  },
  (t) => ({
    userIdx: index('user_drive_grants_user_idx').on(t.userId),
  }),
);

/**
 * Audit trail — one row per successful export to Drive. Never deleted
 * (nothing-unrecoverable law): re-saving an artifact creates a new row and a
 * new dated file, it never overwrites the prior export's record.
 */
export const driveExports = pgTable(
  'drive_exports',
  {
    id: id(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    /** 'resume' | 'cover_letter' | 'corpus_resume' | 'alignment_essay' (the
     *  last one added GAP-CLOSE 2026-07-05 — the essay's own Drive export;
     *  15 chars, fits the existing length(24) with no width change). varchar,
     *  not a DB enum, per this codebase's house convention (jobs.source,
     *  accountKind, dataRegion, entitlementGrants.label all take a plain
     *  varchar over a Postgres enum so a future artifact type is a data
     *  value, not a migration) — mirrors ExportArtifactType in
     *  DriveExportService.ts. */
    artifactType: varchar('artifact_type', { length: 24 }).notNull(),
    /** The source table's own id (resumes.id / coverLetters.id) — those
     *  tables are still text-keyed (FROZEN, untouched here), so this stays
     *  text rather than a typed FK to either. */
    artifactId: text('artifact_id').notNull(),
    driveFileId: text('drive_file_id').notNull(),
    webViewLink: text('web_view_link').notNull(),
    /** The exact filename this export was saved under (buildExportFilename's
     *  output) — an honest record of what was actually written, independent
     *  of any later change to the naming convention. */
    filename: text('filename'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => ({
    userIdx: index('drive_exports_user_idx').on(t.userId),
    artifactIdx: index('drive_exports_artifact_idx').on(t.artifactType, t.artifactId),
  }),
);
